import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// persist only keeps UI preferences (provider), not design data (shapes/boundary)

// ─── Types ─────────────────────────────────────────────────────────────────────

export type MapProvider =
  | 'google_satellite'
  | 'tianditu_satellite'
  | 'tianditu_vector'
  | 'amap_satellite'
  | 'amap_vector'
  | 'osm'
  | 'arcgis'
  | 'google_3d';

export type DrawMode = 'none' | 'rectangle' | 'polygon';

export interface LngLat {
  lng: number;
  lat: number;
}

export interface GeoPolygon {
  type: 'Polygon';
  coordinates: [number, number][][];
}

export interface DrawnShape {
  id: string;
  mode: 'rectangle' | 'polygon';
  geojson: GeoPolygon;
  /** pixel coords at the time of drawing, for UI reference */
  label?: string;
}

// ─── State ─────────────────────────────────────────────────────────────────────

interface MapState {
  center: LngLat;
  zoom: number;
  provider: MapProvider;
  drawMode: DrawMode;
  shapes: DrawnShape[];
  /** The active / most-recent selection (used as project boundary) */
  selectedShape: DrawnShape | null;
  /** Cesium viewer 引用(非持久化, 由 CesiumViewport 注入) */
  cesiumViewer: any | null;

  // ── Actions ─────────────────────────────────────────────────────────────────
  setCenter: (center: LngLat) => void;
  setZoom: (zoom: number) => void;
  setProvider: (provider: MapProvider) => void;
  setDrawMode: (mode: DrawMode) => void;
  addShape: (shape: DrawnShape) => void;
  removeShape: (id: string) => void;
  clearShapes: () => void;
  selectShape: (shape: DrawnShape | null) => void;
  setCesiumViewer: (viewer: any | null) => void;
  initializeDefaultProvider: (language?: string) => void;
}

const MAP_STORE_VERSION = 1;

function migrateProvider(provider?: string): MapProvider {
  if (!provider || provider === 'tianditu_satellite') {
    return 'google_satellite';
  }
  return provider as MapProvider;
}

// ─── Store ─────────────────────────────────────────────────────────────────────

export const useMapStore = create<MapState>()(
  persist(
    (set) => ({
      center: { lng: 116.4074, lat: 39.9042 }, // Beijing default
      zoom: 5,
      provider: 'google_satellite',
      drawMode: 'none',
      shapes: [],
      selectedShape: null,
      cesiumViewer: null,

      setCenter: (center) => set({ center }),
      setZoom: (zoom) => set({ zoom: Math.max(1, Math.min(20, zoom)) }),
      setProvider: (provider) => set({ provider }),
      setDrawMode: (drawMode) => set({ drawMode }),

      addShape: (shape) =>
        set((state) => ({
          shapes: [...state.shapes, shape],
          selectedShape: shape,
          drawMode: 'none',
        })),

      removeShape: (id) =>
        set((state) => ({
          shapes: state.shapes.filter((s) => s.id !== id),
          selectedShape:
            state.selectedShape?.id === id ? null : state.selectedShape,
        })),

      clearShapes: () => set({ shapes: [], selectedShape: null }),
      selectShape: (shape) => set({ selectedShape: shape }),
      setCesiumViewer: (viewer) => set({ cesiumViewer: viewer }),
      initializeDefaultProvider: (language?: string) => {
        // Initialize provider based on language, if not already persisted
        const isChinese = language ? language.startsWith('zh') : navigator.language?.startsWith('zh') ?? false;
        const defaultProvider: MapProvider = isChinese ? 'tianditu_satellite' : 'google_satellite';
        set((state) => {
          // Only set default if provider is still at initial hardcoded value or missing
          if (!state.provider || state.provider === 'google_satellite') {
            return { provider: defaultProvider };
          }
          return state;
        });
      },
    }),
    {
      name: 'sunsim-map-store',
      version: MAP_STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        provider: state.provider,
        // shapes/selectedShape 不再存前端，从后端 config_json 加载
      }),
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as { provider?: string };
        return {
          provider: migrateProvider(state.provider),
        };
      },
    },
  ),
);
